import React, { useMemo } from 'react';
import clsx from 'clsx';
import Image from 'next/image';
import Link from 'next/link';
import { UserHoverCardContent } from '@components/Feed/User/UserHoverCardContent';
import { ThreeDotsDropdown } from '@components/basics/Dropdowns/ThreeDotsDropdown';
import { FollowButton } from '@components/basics/FollowButton';
import { Avatar } from '@components/basics/Images/Avatar';
import { DateTimeTooltip } from '@components/main-components/datetime-tooltip';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { FeedCuratorTooltip } from '@components/main-components/tooltip/FeedCuratorTooltip';
import { VerifiedTooltip } from '@components/main-components/tooltip/VerifiedTooltip';
import { UserHoverCard } from '@components/main-components/user/UserHoverCard';
import { MediaQuery } from '@context/mediaQuery';
import {
  FeedActivityHeaderChannelInfoFragmentDoc,
  type FragmentType,
  type Maybe,
} from '@graphql/reactQuery';
import { getFragment, gql, UserActivityDetailFragmentDoc } from '@graphql/reactQuery';
import { SPACE } from '@utils/constants';
import { getActivityTimestamp } from '@utils/helpers';
import { webappUriPath } from '@utils/url';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment FeedActivityHeaderChannelInfo on Channel {
    id
    name
    webappUri
    staticProfileImage {
      id
      url
    }
    profileImage {
      id
      url
    }
  }
`);

export const FeedActivityHeader = ({
  description,
  dropdownContent,
  showFeedCuratorBadge,
  showFollowButton,
  timestamp,
  userFrag,
  channelFrag,
}: {
  description: string;
  dropdownContent: React.ReactNode;
  showFeedCuratorBadge: boolean;
  showFollowButton: boolean;
  timestamp: number;
  channelFrag: Maybe<FragmentType<FeedActivityHeaderChannelInfoFragmentDoc>>;
  userFrag: FragmentType<UserActivityDetailFragmentDoc>;
}) => {
  const user = getFragment(UserActivityDetailFragmentDoc, userFrag);
  const channel = getFragment(FeedActivityHeaderChannelInfoFragmentDoc, channelFrag);
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
  const { username, artist, avatar, publicAddress, webappUri } = user;
  const timestampText = getActivityTimestamp(timestamp);
  const { tablet } = MediaQuery.useContainer();

  const userHoverCardContent = useMemo(() => <UserHoverCardContent userId={user.id} />, [user.id]);

  const profileImageUri = channel?.staticProfileImage?.url ?? channel?.profileImage?.url;

  return (
    <div className="flex flex-00auto items-center gap-2 md:gap-4" data-feed-clickable-div>
      {/**
       * Avatar
       */}
      <UserHoverCard
        trigger={
          <div>
            <Avatar
              src={avatar?.url}
              username={username}
              size={tablet ? 48 : 40}
              borderRadius={999}
              walletAddress={publicAddress}
              hasBorder
              webappUri={webappUri}
            />
          </div>
        }
        userHoverCardContent={userHoverCardContent}
      />

      {/**
       * Username, timestamp, description
       */}
      <div className="flex flex-col gap-0.5 " data-feed-clickable-div>
        <div className="self-start">
          <UserHoverCard
            trigger={
              <Link href={webappUriPath(webappUri)}>
                <div className="flex gap-1">
                  <div className="font-base text-base-l font-semibold text-base800">{username}</div>
                  {!!artist && <VerifiedTooltip portal />}
                  {showFeedCuratorBadge && <FeedCuratorTooltip portal />}
                </div>
              </Link>
            }
            userHoverCardContent={userHoverCardContent}
          />
        </div>

        <div
          className={clsx(
            channel && 'mt-1',
            'flex items-center self-start font-base text-base-m font-medium text-neutral500 lg:h-6',
          )}
          data-feed-clickable-div
        >
          {channel ? (
            <p data-feed-clickable-div className="flex w-full flex-row items-center">
              {description}
              {SPACE}in{SPACE}
              <AccessibleLink pathnameUri={webappUriPath(channel.webappUri)}>
                <div className="flex flex-row gap-1 rounded-md bg-neutral100 p-1">
                  {profileImageUri && (
                    <Image
                      src={profileImageUri}
                      width={16}
                      height={16}
                      alt={`Profile image for channel ${channel.name}`}
                      className="rounded-sm"
                    />
                  )}
                  <span className="text-base900">{channel.name}</span>
                </div>
              </AccessibleLink>
            </p>
          ) : (
            <p data-feed-clickable-div>{description}</p>
          )}
          <DateTimeTooltip dateTime={new Date(timestamp)}>
            <span className="timestamp" data-feed-clickable-div>
              {SPACE}•{SPACE}
              {timestampText}
            </span>
          </DateTimeTooltip>
        </div>
      </div>

      <div className="flex-1" data-feed-clickable-div />

      {/**
       * Optional dropdown
       */}
      {dropdownContent && (
        <ThreeDotsDropdown
          setIsDropdownOpen={setIsDropdownOpen}
          isDropdownOpen={isDropdownOpen}
          dropdownProps={{
            align: 'end',
            queue: true,
          }}
        >
          {dropdownContent}
        </ThreeDotsDropdown>
      )}

      {/**
       * Optional dropdown
       */}
      {!dropdownContent && showFollowButton && (
        <div className="flex-00auto">
          <FollowButton
            sourceLocation="Post Page Header"
            size="XS"
            userId={user.id}
            variant="notification"
            useIcon={!tablet}
            artistId={user.artist?.id}
          />
        </div>
      )}
    </div>
  );
};
